import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Star, 
  Flame, 
  Trophy, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Trash2,
  RotateCcw,
  Plus,
  Calendar,
  Send
} from 'lucide-react';
import { getLocalTodayDateString } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminChildTasks() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [child, setChild] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, submission: null });
  const [deleting, setDeleting] = useState({});
  const [resetDialog, setResetDialog] = useState({ open: false });
  const [resetting, setResetting] = useState(false);
  const [adminSubmissionDialog, setAdminSubmissionDialog] = useState({ open: false });
  const [submittingAdminTask, setSubmittingAdminTask] = useState(false);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [adminSubmissionForm, setAdminSubmissionForm] = useState({
    task_id: '',
    submission_date: getLocalTodayDateString(),
    notes: ''
  });



  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/children/${childId}/tasks`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setChild(data.child);
        setSubmissions(data.submissions);
      }
    } catch (error) {
      console.error('Failed to fetch child tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [childId, getAuthHeaders]);

  const fetchAvailableTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/all`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const tasks = await response.json();
        const childAge = child?.age;
        const filteredTasks = tasks.filter(task => {
          if (task.gender_target !== 'All' && task.gender_target !== child?.gender) return false;
          if (task.min_age && childAge < task.min_age) return false;
          if (task.max_age && childAge > task.max_age) return false;
          if (task.is_expired) return false;
          return true;
        });
        setAvailableTasks(filteredTasks);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load available tasks');
    }
  }, [getAuthHeaders, child]);

  const handleOpenAdminSubmissionDialog = () => {
    setAdminSubmissionForm({
      task_id: '',
      submission_date: getLocalTodayDateString(),
      notes: ''
    });
    fetchAvailableTasks();
    setAdminSubmissionDialog({ open: true });
  };

  const handleAdminSubmission = async () => {
    if (!adminSubmissionForm.task_id || !adminSubmissionForm.submission_date) {
      toast.error('Please select a task and date');
      return;
    }

    setSubmittingAdminTask(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/submissions`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          task_id: adminSubmissionForm.task_id,
          child_id: childId,
          submission_date: adminSubmissionForm.submission_date,
          notes: adminSubmissionForm.notes
        })
      });

      if (response.ok) {
        const result = await response.json();
        const task = availableTasks.find(t => t.task_id === adminSubmissionForm.task_id);
        toast.success(`Task "${task?.name}" submitted for ${child?.first_name}! +${task?.points} points`);
        setAdminSubmissionDialog({ open: false });
        fetchData(); // Refresh the data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit task');
      }
    } catch (error) {
      toast.error('Failed to submit task');
    } finally {
      setSubmittingAdminTask(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusBadge = (status, adminSubmitted) => {
    if (adminSubmitted) {
      return (
        <Badge className="bg-purple-100 text-purple-800 rounded-full">
          <Send className="w-3 h-3 mr-1" />
          Admin Submitted
        </Badge>
      );
    }
      switch (status) {
      case 'submitted':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 rounded-full">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'approved':
        return (
          <Badge className="bg-green-100 text-green-800 rounded-full">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-100 text-red-800 rounded-full">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleDeleteSubmission = (submission) => {
    setDeleteDialog({ open: true, submission });
  };

  const confirmDeleteSubmission = async () => {
    if (!deleteDialog.submission) return;
    
    setDeleting(prev => ({ ...prev, [deleteDialog.submission.submission_id]: true }));
    
    try {
      const response = await fetch(`${API_URL}/api/submissions/${deleteDialog.submission.submission_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Submission deleted successfully');
        setDeleteDialog({ open: false, submission: null });
        // Refresh data to get updated child stats
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete submission');
      }
    } catch (error) {
      toast.error('Failed to delete submission');
    } finally {
      setDeleting(prev => ({ ...prev, [deleteDialog.submission.submission_id]: false }));
    }
  };

  const handleResetProgress = () => {
    setResetDialog({ open: true });
  };

  const confirmResetProgress = async () => {
    setResetting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/children/${childId}/reset`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Progress reset successfully');
        setResetDialog({ open: false });
        fetchData(); // Refresh all data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset progress');
      }
    } catch (error) {
      toast.error('Failed to reset progress');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!child) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-[#6B7280]">Child not found</p>
          <Button onClick={() => navigate('/admin')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="text-[#6B7280] hover:text-[#264653]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenAdminSubmissionDialog}
              className="border-[#08513F] text-[#08513F] hover:bg-[#08513F] hover:text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Submit Past Task
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleResetProgress}
              disabled={resetting}
            >
              {resetting ? (
                <div className="w-4 h-4 border-2 border-white-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Reset Progress
            </Button>
          </div>
        </div>

        {/* Child Profile Card */}
        <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-xl overflow-hidden">
          <div className={`h-32 ${child.gender === 'Muslim' ? 'bg-islamic-green' : 'bg-gradient-to-r from-[#E76F51] to-[#F4A261]'}`} />
          <CardContent className="pt-0 -mt-16 px-8 pb-8">
            <div className="flex flex-col md:flex-row md:items-end gap-6">
              <Avatar className="h-32 w-32 border-4 border-white shadow-xl">
                <AvatarImage src={child.profile_image} />
                <AvatarFallback className={`text-3xl font-bold ${
                  child.gender === 'Muslim' ? 'bg-[#08513F]' : 'bg-[#E76F51]'
                } text-white`}>
                  {child.first_name?.charAt(0)}{child.last_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 md:bg-white md:rounded-2xl md:p-4">
                <h1 className="text-3xl font-bold text-[#264653] font-outfit">
                  {child.first_name} {child.last_name}
                </h1>
                <p className="text-[#6B7280]">{child.gender} • Age {child.age}</p>
                <p className="text-sm text-[#6B7280]">Parent: {child.parent_email}</p>
              </div>

              <div className="flex gap-4">
                <div className="text-center p-4 bg-[#FFFBEB] rounded-2xl">
                  <Star className="w-6 h-6 text-[#D4AF37] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#264653] font-outfit">{child.total_points}</p>
                  <p className="text-xs text-[#6B7280]">Points</p>
                </div>
                <div className="text-center p-4 bg-[#FFFBEB] rounded-2xl">
                  <Flame className="w-6 h-6 text-[#E76F51] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#264653] font-outfit">{child.current_streak}</p>
                  <p className="text-xs text-[#6B7280]">Streak</p>
                </div>
                <div className="text-center p-4 bg-[#FFFBEB] rounded-2xl">
                  <Trophy className="w-6 h-6 text-[#08513F] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#264653] font-outfit">{child.longest_streak}</p>
                  <p className="text-xs text-[#6B7280]">Best</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Task History */}
        <div>
          <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-4">Task History</h2>
          
          {submissions.length === 0 ? (
            <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
              <CheckCircle2 className="w-16 h-16 text-[#08513F]/30 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#264653] font-outfit mb-2">No Tasks Submitted</h3>
              <p className="text-[#6B7280]">This child hasn't submitted any tasks yet.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {submissions.map((submission, index) => (
                <Card 
                  key={submission.submission_id}
                  className="bg-white rounded-2xl border-[#D4AF37]/20 shadow-md animate-slide-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-bold text-[#264653] font-outfit">
                            {submission.task?.name || 'Unknown Task'}
                          </h3>
                          {getStatusBadge(submission.status, submission.admin_submitted)}
                        </div>
                        <p className="text-sm text-[#6B7280]">
                          {submission.task?.description}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-[#D4AF37] font-semibold">
                            <Star className="w-3 h-3 inline mr-1" />
                            {submission.task?.points || 0} pts
                          </span>
                          <span className="text-[#6B7280]">
                            {new Date(submission.submission_date).toLocaleString()}
                          </span>
                        </div>
                        {submission.admin_submitted && (
                          <div className="mt-2 p-2 bg-purple-50 rounded-lg">
                            <p className="text-xs text-purple-700">
                              <strong>Admin Submitted:</strong> {submission.admin_email}
                              {submission.notes && ` - ${submission.notes}`}
                            </p>
                          </div>
                        )}
                        {submission.rejection_reason && (
                          <p className="text-sm text-red-500 mt-2">
                            Rejection reason: {submission.rejection_reason}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteSubmission(submission)}
                        disabled={deleting[submission.submission_id]}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      >
                        {deleting[submission.submission_id] ? (
                          <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        {deleteDialog.open && (
          <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Submission</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this submission?
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-[#264653]">{deleteDialog.submission?.task?.name}</p>
                    <p className="text-sm text-[#6B7280] mt-1">{deleteDialog.submission?.task?.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="text-[#D4AF37] font-semibold">
                        {deleteDialog.submission?.task?.points || 0} pts
                      </span>
                      <span className="text-[#6B7280]">
                        Status: {deleteDialog.submission?.status}
                      </span>
                    </div>
                    {deleteDialog.submission?.status === 'approved' && (
                      <p className="text-sm text-orange-600 mt-2 font-medium">
                        ⚠️ This will deduct {deleteDialog.submission?.task?.points} points and recalculate the streak.
                      </p>
                    )}
                  </div>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setDeleteDialog({ open: false, submission: null })}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmDeleteSubmission}
                  disabled={deleting[deleteDialog.submission?.submission_id]}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting[deleteDialog.submission?.submission_id] ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Delete Submission'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        
        {/* Reset Progress Dialog */}
        {resetDialog.open && (
          <Dialog open={resetDialog.open} onOpenChange={(open) => setResetDialog({ ...resetDialog, open })}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-red-600">⚠️ Reset Progress</DialogTitle>
                <DialogDescription>
                  This will permanently reset ALL of {child?.first_name}'s progress:
                  <div className="mt-3 p-3 bg-red-50 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Total points:</span>
                      <span className="font-semibold">{child?.total_points || 0} → 0</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Current streak:</span>
                      <span className="font-semibold">{child?.current_streak || 0} → 0</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Longest streak:</span>
                      <span className="font-semibold">{child?.longest_streak || 0} → 0</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>All submissions:</span>
                      <span className="font-semibold">{submissions.length} → 0</span>
                    </div>
                  </div>
                  <p className="text-sm text-red-600 font-medium mt-3">
                    This action cannot be undone.
                  </p>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setResetDialog({ open: false })}
                  disabled={resetting}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmResetProgress}
                  disabled={resetting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {resetting ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Reset Progress'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        
        {/* Admin Submission Dialog */}
        {adminSubmissionDialog.open && (
          <Dialog open={adminSubmissionDialog.open} onOpenChange={(open) => setAdminSubmissionDialog({ ...adminSubmissionDialog, open })}>
            <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-[#264653] font-outfit">
                  Submit Past Task for {child?.first_name}
                </DialogTitle>
                <DialogDescription>
                  Submit any task for {child?.first_name} on any date. Tasks will be auto-approved and points awarded immediately.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="task-select">Select Task *</Label>
                  <Select
                    value={adminSubmissionForm.task_id}
                    onValueChange={(value) => setAdminSubmissionForm({ ...adminSubmissionForm, task_id: value })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Choose a task..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTasks.map((task) => (
                        <SelectItem key={task.task_id} value={task.task_id}>
                          <div className="flex items-center gap-2">
                            <span>{task.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {task.points} pts
                            </Badge>
                            {task.frequency === 'daily' ? (
                              <Badge variant="outline" className="text-xs">Daily</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">One-Time</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="submission-date">Submission Date *</Label>
                  <Input
                    id="submission-date"
                    type="date"
                    value={adminSubmissionForm.submission_date}
                    onChange={(e) => setAdminSubmissionForm({ ...adminSubmissionForm, submission_date: e.target.value })}
                    className="rounded-xl"
                    max={getLocalTodayDateString()}
                  />
                  <p className="text-xs text-[#6B7280]">
                    Select the date when the task was completed
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin-notes">Notes (Optional)</Label>
                  <Textarea
                    id="admin-notes"
                    value={adminSubmissionForm.notes}
                    onChange={(e) => setAdminSubmissionForm({ ...adminSubmissionForm, notes: e.target.value })}
                    placeholder="Add any notes about this submission..."
                    className="rounded-xl"
                    rows={3}
                  />
                  <p className="text-xs text-[#6B7280]">
                    These notes will be visible to the parent
                  </p>
                </div>

                {adminSubmissionForm.task_id && (
                  <div className="p-3 bg-[#FFFBEB] rounded-lg">
                    <p className="text-sm font-medium text-[#264653]">
                      Task Preview:
                    </p>
                    {(() => {
                      const selectedTask = availableTasks.find(t => t.task_id === adminSubmissionForm.task_id);
                      return selectedTask ? (
                        <div className="mt-2">
                          <p className="text-sm text-[#6B7280]">{selectedTask.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[#D4AF37] font-semibold text-sm">
                              <Star className="w-3 h-3 inline mr-1" />
                              {selectedTask.points} points
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {selectedTask.frequency === 'daily' ? 'Daily' : 'One-Time'}
                            </Badge>
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setAdminSubmissionDialog({ open: false })}
                  disabled={submittingAdminTask}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAdminSubmission}
                  disabled={submittingAdminTask || !adminSubmissionForm.task_id || !adminSubmissionForm.submission_date}
                  className="bg-[#08513F] hover:bg-[#064031] text-white"
                >
                  {submittingAdminTask ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Task
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
